import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// Filter keys that describe *what* is shown. Changing any of them has to send
// the user back to page 1 — searching while on page 3 previously asked the
// server for page 3 of a brand-new result set and rendered "Records not found".
const RESETS_PAGE = new Set([
    "keyword", "limit", "auction", "admin_id", "admin",
    "start_date", "end_date", "sort_by", "sort_order",
    "eligible_to_bid", "blocked", "active",
]);

/**
 * Table filter state, seeded from the query string.
 *
 * core.useQuery returned a URLSearchParams but every caller read it with plain
 * property access (`query.page`), which is always undefined — so deep links
 * and bookmarks never restored anything. This reads it properly.
 */
export default function useTableFilter(defaults) {
    const { search } = useLocation();

    const initial = useMemo(() => {
        const params = new URLSearchParams(search);
        const seeded = { ...defaults };

        Object.keys(defaults).forEach((key) => {
            const value = params.get(key);
            if (value === null) return;
            if (typeof defaults[key] === "number") {
                const parsed = Number.parseInt(value, 10);
                if (Number.isFinite(parsed)) seeded[key] = parsed;
            } else if (typeof defaults[key] === "boolean") {
                seeded[key] = value === "true";
            } else {
                seeded[key] = value;
            }
        });

        return seeded;
        // Seeded once, from the URL the page was opened with.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [filter, setFilterState] = useState(initial);

    const setFilter = useCallback((update) => {
        setFilterState((prev) => {
            const next = typeof update === "function" ? update(prev) : { ...prev, ...update };

            const changedKey = Object.keys(next).find(
                (key) => next[key] !== prev[key] && RESETS_PAGE.has(key)
            );

            return changedKey ? { ...next, page: 1 } : next;
        });
    }, []);

    // Clamps `page` so deleting the last row on the final page doesn't strand
    // the user on a page that no longer exists.
    const clampPage = useCallback((total) => {
        setFilterState((prev) => {
            const pageCount = Math.max(1, Math.ceil(total / prev.limit));
            return prev.page > pageCount ? { ...prev, page: pageCount } : prev;
        });
    }, []);

    return { filter, setFilter, clampPage };
}
